import { ReactElement, useCallback, useMemo } from 'react'
import { find, isFunction, filter, toNumber, reject } from 'lodash-es'
import { useTheme } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import InstancedBoxMesh from '../BoxMesh/InstancedBoxMesh'
import { getPointAtPercentage } from '../utils'
import CylinderMesh from './CylinderMesh'

interface PositionedSupport {
  item: ElementSupportItem
  position: ImmutableVector3
  domain: Domain
  relativePosition: number
}

interface PositionedTarget extends PositionedSupport {
  supportItem: ElementSupportItem
  supportPosition: ImmutableVector3
}

const InstancedConnectorMesh = ({
  data,
  domains,
  onClick,
  transmitterGuid,
  hiddenElementGuids,
  disableClick,
  pointConnectorKind = 'tensile',
}: TransmitterMeshProps & { hiddenElementGuids?: string[] }): ReactElement => {
  const theme = useTheme()
  const supportItems = useMemo(() => {
    const supportPositions = data.element_supports.reduce((acc: PositionedSupport[], support) => {
      const { domain: embeddedDomain, domain_guid, relative_position } = support

      // Prefer the domain embedded on the support (as the line-support mesh does); the passed
      // `domains` array does not always contain every support's domain_guid.
      const domain = embeddedDomain || find(domains, ['guid', domain_guid])

      if (!domain) return acc

      return [
        ...acc,
        {
          position: getPointAtPercentage(domain.start, domain.end, toNumber(relative_position)),
          item: support,
          domain,
          relativePosition: toNumber(relative_position),
        },
      ]
    }, [])

    return supportPositions
  }, [data.element_supports, domains])

  const supportTargetMap = useMemo(() => {
    return supportItems.reduce(
      (
        acc: {
          [key: string]: boolean
        },
        { item },
      ) => {
        const hasTarget = !!find(data.support_targets, ['support_guid', item.guid])

        return {
          ...acc,
          [item.guid]: hasTarget,
        }
      },
      {},
    )
  }, [supportItems, data.support_targets])

  const targetItems = useMemo(() => {
    return supportItems.reduce(
      (acc: PositionedTarget[], { item: supportItem, position: supportPosition }) => {
        const { guid: supportGuid } = supportItem
        const items = filter(data.support_targets, ['support_guid', supportGuid]).reduce(
          (itemAcc: PositionedTarget[], { target_guid }) => {
            const target = find(data.element_targets, ['guid', target_guid])

            if (!target) return itemAcc

            const { domain: embeddedDomain, domain_guid, relative_position } = target
            const domain = embeddedDomain || find(domains, ['guid', domain_guid])

            if (!domain) return itemAcc

            return [
              ...itemAcc,
              {
                position: getPointAtPercentage(
                  domain.start,
                  domain.end,
                  toNumber(relative_position),
                ),
                item: target,
                supportItem: supportItem,
                supportPosition,
                domain,
                relativePosition: toNumber(relative_position),
              },
            ]
          },
          [],
        )

        return [...acc, ...items]
      },
      [],
    )
  }, [supportItems, data.support_targets, data.element_targets, domains])

  const handleConnectorClick = useCallback(
    (event: ThreeEvent<MouseEvent>, connector: ElementSupportItem) => {
      if (disableClick) return

      event.stopPropagation()

      if (!isFunction(onClick)) return

      onClick(connector, event)
    },
    [onClick, disableClick],
  )

  const supportItemsWithoutActive = useMemo(
    () => reject(supportItems, supportItem => supportItem.item.guid === transmitterGuid),
    [supportItems, transmitterGuid],
  )

  const targetItemsWithoutActive = useMemo(
    () => reject(targetItems, targetItem => targetItem.supportItem.guid === transmitterGuid),
    [targetItems, transmitterGuid],
  )

  const activeSupportItems = useMemo(
    () => filter(supportItems, supportItem => supportItem.item.guid === transmitterGuid),
    [supportItems, transmitterGuid],
  )

  const activeTargetItems = useMemo(
    () => filter(targetItems, targetItem => targetItem.supportItem.guid === transmitterGuid),
    [targetItems, transmitterGuid],
  )

  const supportItemsWithTarget = useMemo(
    () => supportItemsWithoutActive.filter(({ item }) => supportTargetMap[item.guid]),
    [supportItemsWithoutActive, supportTargetMap],
  )

  const supportItemsWithoutTarget = useMemo(
    () => supportItemsWithoutActive.filter(({ item }) => !supportTargetMap[item.guid]),
    [supportItemsWithoutActive, supportTargetMap],
  )

  const supportInstancesWithTarget = useMemo(
    () =>
      supportItemsWithTarget.map(({ position, domain, relativePosition }) => ({
        position,
        domain,
        relativePosition,
        connectorKind: pointConnectorKind,
        isTarget: false,
      })),
    [pointConnectorKind, supportItemsWithTarget],
  )

  const supportInstancesWithoutTarget = useMemo(
    () =>
      supportItemsWithoutTarget.map(({ position, domain, relativePosition }) => ({
        position,
        domain,
        relativePosition,
        connectorKind: pointConnectorKind,
        isTarget: false,
      })),
    [pointConnectorKind, supportItemsWithoutTarget],
  )

  const targetInstances = useMemo(
    () =>
      targetItemsWithoutActive.map(({ position, domain, relativePosition }) => ({
        position,
        domain,
        relativePosition,
        connectorKind: pointConnectorKind,
        isTarget: true,
      })),
    [pointConnectorKind, targetItemsWithoutActive],
  )

  return (
    <>
      <InstancedBoxMesh
        instances={supportInstancesWithTarget}
        color={theme.scenePalette.elements3d.supports}
        onClick={
          disableClick
            ? undefined
            : (event, index) => handleConnectorClick(event, supportItemsWithTarget[index].item)
        }
        hiddenElementGuids={hiddenElementGuids}
        getElementGuid={index => supportItemsWithTarget[index]?.item.element_guid}
      />

      <InstancedBoxMesh
        instances={supportInstancesWithoutTarget}
        color={theme.scenePalette.invalid}
        onClick={
          disableClick
            ? undefined
            : (event, index) => handleConnectorClick(event, supportItemsWithoutTarget[index].item)
        }
        hiddenElementGuids={hiddenElementGuids}
        getElementGuid={index => supportItemsWithoutTarget[index]?.item.element_guid}
      />

      <InstancedBoxMesh
        instances={targetInstances}
        color={theme.scenePalette.elements3d.targets}
        onClick={
          disableClick
            ? undefined
            : (event, index) =>
                handleConnectorClick(event, targetItemsWithoutActive[index].supportItem)
        }
        hiddenElementGuids={hiddenElementGuids}
        getElementGuid={index => targetItemsWithoutActive[index]?.supportItem.element_guid}
      />

      {activeSupportItems.map(({ position, item, domain, relativePosition }, index) => {
        return (
          <CylinderMesh
            onClick={disableClick ? undefined : event => handleConnectorClick(event, item)}
            key={index}
            position={position}
            domain={domain}
            relativePosition={relativePosition}
            connectorKind={pointConnectorKind}
            isActive
            color={
              supportTargetMap[item.guid]
                ? theme.scenePalette.elements3d.supports
                : theme.scenePalette.invalid
            }
            disableDepthTest
            visible={!hiddenElementGuids || !hiddenElementGuids.includes(item.element_guid)}
          />
        )
      })}
      {activeTargetItems.map(
        ({ position, supportItem, supportPosition, domain, relativePosition }, index) => {
          return (
            <CylinderMesh
              onClick={disableClick ? undefined : event => handleConnectorClick(event, supportItem)}
              key={index}
              position={position}
              supportPosition={supportPosition}
              domain={domain}
              relativePosition={relativePosition}
              connectorKind={pointConnectorKind}
              isActive
              isTarget
              color={theme.scenePalette.elements3d.targets}
              disableDepthTest
              visible={
                !hiddenElementGuids || !hiddenElementGuids.includes(supportItem.element_guid)
              }
            />
          )
        },
      )}
    </>
  )
}

export default InstancedConnectorMesh
